import React, { ReactElement, useState, useRef, useEffect } from 'react';
import { FieldProps, ErrorMessage, useFormikContext, getIn } from 'formik';
import classnames from 'classnames';
import { tileReview } from '@interfaces/interfaces';
import TickIcon from '../../images/tickIcon.svg';
import DownArrow from '../../images/downArrow.svg';
import styles from './TileRadioAccessibleReview.module.scss';

interface iProps extends FieldProps {
  title?: string;
  isMandatory: boolean;
  tileData: tileReview[];
  orientationRow: boolean;
  mobileCollapseRow?: boolean;
  classname?: string;
  wrapTiles?: boolean;
  tileChanged?: () => void;
  alwaysOpen?: boolean;
  isPUIneligible?: boolean;
  validateFieldName: string;
  isMultiSelectable?: boolean;
}

const TileRadioAccessibleReview = ({ title, isMandatory, field, form, tileData, orientationRow, mobileCollapseRow = true, classname = '', wrapTiles = false, tileChanged, alwaysOpen = false, isPUIneligible = false, validateFieldName, isMultiSelectable = false }: iProps): ReactElement => {
  const { errors, touched, values }: { errors: any; touched: any, values: any } = useFormikContext();
  const formikContext = useFormikContext();
  const hasError = getIn(errors, validateFieldName);
  const touchedError = getIn(touched, validateFieldName);
  const tileLength = tileData.length;
  const [accordionOpen, setAccordionOpen] = useState<string | null>(getIn(values, validateFieldName)[0]);
  const prevAccordionOpenRef = useRef<string | null>(null);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const prevAccordionOpen = prevAccordionOpenRef.current;
  // Create a refs object
  const refs: { [key: string]: React.RefObject<HTMLDivElement | null> } = tileData.reduce((acc: { [key: string]: React.RefObject<HTMLDivElement | null> }, tileItem, index) => {
    acc[`${field.name}${index}`] = React.createRef();
    return acc;
  }, {});
  const calculateHeight = (tileItemValue: string) => {
    const tileItemValueA = tileItemValue === accordionOpen && tileItemValue !== values.closeTheOption ? `${heights[tileItemValue]}px` : '0';
    return tileItemValueA;
  };
  const handleTickIconClick = (currentSelected: string) => {
    if (isMultiSelectable) {
      const indexToRemove = getIn(values, validateFieldName).indexOf(currentSelected);
      getIn(values, validateFieldName).splice(indexToRemove, 1);
      formikContext.setFieldValue('closeTheOption', '');
      setAccordionOpen(currentSelected);
    }
  };
  let iconDivContent: React.ReactElement;
  const returnIconContent = (currentSelected: string, index: number): React.ReactElement => {
    if (alwaysOpen) {
      iconDivContent = (
        <div className={styles.iconDiv}>
          <img src={DownArrow} className={styles.arrowIcon} alt="arrow" />
        </div>
      );
    } else if (getIn(values, validateFieldName) && getIn(values, validateFieldName).includes(currentSelected)) {
      iconDivContent = (
        <div
          className={styles.iconDiv}
          role="button"
          tabIndex={-1}
          onKeyDown={(e) => { if (e.key === ' ' || e.key === 'Enter') { handleTickIconClick(currentSelected); } }}
          onClick={() => handleTickIconClick(currentSelected)}>
          <img
            src={TickIcon}
            className={classnames({
            [styles.tickIcon]: true,
            [styles.tickIconCursor]: isMultiSelectable,
            })}
            alt="tick"
            />
        </div>
      );
    } else {
      iconDivContent = (
        <div className={styles.iconDiv}>
          <img
            src={DownArrow}
            className={classnames({
              [styles.arrowIcon]: true,
              [styles.iconClicked]: accordionOpen === (field.name + index) && accordionOpen !== values.closeTheOption,
              [styles.iconInitiated]: (accordionOpen !== (field.name + index) || accordionOpen === values.closeTheOption),
            })}
            alt="Expand/Contract the question" />
        </div>
      );
    }
    return iconDivContent;
  };
  const getBrowser = () => {
    const { userAgent } = navigator;
    let browser = 'unknown';
    if (/chrome/i.test(userAgent)) {
      browser = 'chrome';
    } else if (/safari/i.test(userAgent)) {
      browser = 'safari';
    }
    return browser;
  };

  useEffect(() => {
    if (accordionOpen) {
      // Now that the accordion content is visible, calculate its height
      const height = refs[accordionOpen].current?.getBoundingClientRect().height || 0;
      setHeights((prevHeights) => ({
        ...prevHeights,
        [accordionOpen]: height,
      }));
    } else {
      setHeights({});
    }
    prevAccordionOpenRef.current = accordionOpen;
  }, [accordionOpen, values.closeTheOption, getIn(values, validateFieldName), values.errorOrInfoAppearsInAccordion, field.value]);

  return (
    <>
      {title && (
        <div
          className={classnames({
            [styles.title]: true,
            [styles.titleError]: hasError && touchedError,
          })}>
          {title}
          {!isMandatory && <span className={styles.optional}> ( optional )</span>}
        </div>
      )}
      <div
        className={classnames({
          [styles.tileContainer]: true,
          [styles.wrapTiles]: wrapTiles,
          [styles.orientationRow]: orientationRow && !mobileCollapseRow,
          [styles.orientationRowCollapse]: orientationRow && mobileCollapseRow,
          [styles.orientationColumn]: !orientationRow,
          [styles.tileItemExpand]: tileLength > 2,
        })}
        role="radiogroup"
      >
        {tileData
          .map((tileItem, index) => (
            <div
              key={tileItem.value}
              className={classnames({
              [styles.hidden]: (isPUIneligible && index === 3)
            })}
              role="radio"
              tabIndex={0}
              aria-checked="false"
              onKeyDown={() => { if (values.closeTheOption) { formikContext.setFieldValue('closeTheOption', ''); } setAccordionOpen(`${field.name}${index}`); }}
          >
              <div
                key={tileItem.value}
                // tabIndex={0}
                // aria-checked="false"
                // role="radio"
                className={classnames({
                  [styles.tileItem]: true,
                  [styles.selected]: (calculateHeight(`${field.name}${index}`) !== '0' && field.value === tileItem.value) || (getIn(values, validateFieldName).includes(`${field.name}${index}`))
                })}>
                <input
                  type="radio"
                  id={`${field.name}${tileItem.value}`}
                  name={field.name}
                  value={tileItem.value}
                  onKeyDown={(e) => {
                    if (e.key === ' ') {
                      form.setFieldTouched(field.name);
                      form.setFieldValue(field.name, tileItem.value);
                      // handle tileChanged only a tile is already selected and a new tile is now selected
                      if (getIn(values, field.name) && tileItem.value !== getIn(values, field.name) && tileChanged) tileChanged();
                    }
                  }}
                  onClick={() => {
                    formikContext.setFieldValue('closeTheOption', '');
                    if (getBrowser() === 'safari') {
                      setAccordionOpen((previousAccordionOpen) => {
                        if (previousAccordionOpen === `${field.name}${index}`) {
                          // If the accordion is already open, close it
                          return '';
                        }
                          // If the accordion is closed, open it
                          return `${field.name}${index}`;
                      });
                    } else {
                      // eslint-disable-next-line no-lonely-if
                      if (accordionOpen === prevAccordionOpen && values.closeTheOption !== accordionOpen && !(accordionOpen === undefined && prevAccordionOpen === undefined)) {
                        // If the accordion is already open, close it
                        setAccordionOpen('');
                      } else {
                        // If the accordion is closed, open it
                        setAccordionOpen(`${field.name}${index}`);
                      }
                    }
                  }}
                  onChange={() => {
                    form.setFieldTouched(field.name);
                    form.setFieldValue(field.name, tileItem.value);
                    // handle tileChanged only a tile is already selected and a new tile is now selected
                    if (getIn(values, field.name) && tileItem.value !== getIn(values, field.name) && tileChanged) tileChanged();
                  }}
                />
                <label htmlFor={`${field.name}${tileItem.value}`}>
                  <div
                    className={classnames({
                      [styles.tile]: true,
                      [`${classname}`]: true,
                    })}>
                    <div className={styles.tileValue}>
                      <span
                        className={classnames({
                        [styles.selectedText]: (calculateHeight(`${field.name}${index}`) !== '0' && field.value === tileItem.value) || (getIn(values, validateFieldName).includes(`${field.name}${index}`))
                      })}>
                        {tileItem.value}
                      </span>
                      <span className={styles.subTitle}>{tileItem.subTitle}</span>
                    </div>
                    {returnIconContent(`${field.name}${index}`, index)}
                  </div>
                </label>
              </div>
              <div className={styles.accordionContent} style={{ height: calculateHeight(`${field.name}${index}`) }}>
                <div
                  key={`${tileItem.value}.info`}
                  ref={refs[`${field.name}${index}`]}
                  className={styles.divPadding}>
                  <div className={styles.accordContent}>{tileItem.body}</div>
                </div>
              </div>
            </div>
          ))}
      </div>
      <div>
        <ErrorMessage className={styles.errorMsg} name={validateFieldName || field.name} component="span" />
      </div>
    </>
  );
};

export default React.memo(TileRadioAccessibleReview);
